import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import React, { useState, useCallback, useRef, useMemo } from 'react';
import { PiCaretDownLight } from 'react-icons/pi';
import CalendarView from '../components/Tasks/CalendarView';
import { useFeatureTour } from '../hooks/useFeatureTour';
import { FeatureTour, calendarTourSteps } from '../components/shared/FeatureTour';
import {
  fetchCalendarEvents,
  updateCalendarEvent,
  exportCalendarICS,
  importCalendarICS,
  syncCalendarEvents,
  createFeedToken,
  setCalendarFilters,
} from '../store/actions/CalendarActions';
import { showModal, hideModal } from '../store/actions/modalActions';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/index';
import {
  CalendarEvent,
  CalendarEventType,
  CalendarSourceType,
  EVENT_TYPE_COLORS,
} from '../components/Calendar/types';
import { Action } from '../components/shared/TailwindComponents/Header';
import {
  PiDownloadSimpleLight,
  PiUploadSimpleLight,
  PiArrowsClockwiseLight,
  PiLinkLight,
  PiCalendarBlankLight,
  PiMapPinLight,
  PiClockLight,
  PiTagLight,
  PiArrowSquareOutLight,
  PiXLight,
  PiUsersLight,
  PiInfoLight,
  PiFunnelLight,
} from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import Badge from '../components/shared/TailwindComponents/Badge';
import Button from '../components/shared/TailwindComponents/Button';
import ProfileImage from '../components/Header/ProfileImage';
import { formatDateTime } from '../utils/functions';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/* ─── Displayable event type labels ─── */
const EVENT_TYPE_LABELS: Partial<Record<CalendarEventType, string>> = {
  task_deadline: 'Task',
  matter_deadline: 'Matter',
  project_deadline: 'Project',
  hearing: 'Hearing',
  meeting: 'Meeting',
  consultation: 'Consultation',
  filing_deadline: 'Filing',
  statute_of_limitations: 'Limitation',
  client_meeting: 'Client',
  court_date: 'Court',
  reminder: 'Reminder',
  custom: 'Custom',
  task: 'Task',
  matter: 'Matter',
  project: 'Project',
};

/* Badge color mapping from event type to Badge component color */
const EVENT_TYPE_BADGE_COLORS: Partial<Record<CalendarEventType, string>> = {
  task_deadline: 'blue', task: 'blue',
  matter_deadline: 'purple', matter: 'purple',
  project_deadline: 'green', project: 'green',
  hearing: 'red', meeting: 'green', consultation: 'cyan',
  filing_deadline: 'orange', statute_of_limitations: 'red',
  client_meeting: 'cyan', court_date: 'amber',
  reminder: 'yellow', custom: 'gray',
};

const LEGEND_TYPES: CalendarEventType[] = [
  'task_deadline', 'matter_deadline', 'project_deadline', 'reminder', 'custom',
];

const FILTER_EVENT_TYPES: CalendarEventType[] = [
  'task_deadline', 'matter_deadline', 'project_deadline', 'hearing', 'meeting', 'consultation',
  'filing_deadline', 'statute_of_limitations', 'client_meeting', 'court_date', 'reminder', 'custom',
];

const SOURCE_TYPES: CalendarSourceType[] = ['task', 'matter', 'project', 'client', 'manual'];

/* ═══════════════════════════════════════════════════════════════════════
   Event Detail Modal Content
   ═══════════════════════════════════════════════════════════════════════ */

interface EventDetailProps {
  event: CalendarEvent;
  onNavigate: (event: CalendarEvent) => void;
  onClose: () => void;
}

const EventDetailContent: React.FC<EventDetailProps> = ({ event, onNavigate, onClose }) => {
  const { t } = useTranslation();
  const colors = EVENT_TYPE_COLORS[event.event_type] ?? EVENT_TYPE_COLORS.custom;
  const badgeColor = EVENT_TYPE_BADGE_COLORS[event.event_type] ?? 'gray';
  const typeLabel = EVENT_TYPE_LABELS[event.event_type] ?? event.event_type;
  const [peopleOpen, setPeopleOpen] = useState(true);

  const hasSource = event.source_type && event.source_uuid;
  const sourceLabel = event.source_type === 'task'
    ? t('View Task')
    : event.source_type === 'matter'
      ? t('View Matter')
      : event.source_type === 'project'
        ? t('View Project')
        : t('View Source');

  const startLabel = event.start_time ? formatDateTime(event.start_time) : '';
  const endLabel = event.end_time ? formatDateTime(event.end_time) : '';

  // Merge shared_with + collaborators, dedup by user_id/email
  const peopleRaw = [
    ...((event as any).collaborators || []),
    ...(event.shared_with || []),
  ];
  const seen = new Set<string>();
  const people = peopleRaw.filter((p: any) => {
    const key = String(p.user_id ?? p.uuid ?? p.email ?? p.Email ?? p.Name ?? Math.random());
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col max-h-[75vh] min-h-[75vh]">
      {/* Header (sticky / non-scrolling) */}
      <div className={`flex items-start gap-3 p-4 rounded-lg mx-6 flex-shrink-0 ${colors.bg} ${colors.darkBg}`}>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight break-words">
            {event.title}
          </h2>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <Badge color={badgeColor as any}>{typeLabel}</Badge>
            {event.status && event.status !== 'active' && (
              <Badge color={event.status === 'completed' ? 'green' : event.status === 'cancelled' ? 'red' : 'yellow'}>
                {event.status}
              </Badge>
            )}
            {event.visibility && event.visibility !== 'private' && (
              <Badge color="gray">{event.visibility}</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable middle */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-main px-6 pt-4 pb-2 min-h-0">

      {/* Detail grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {startLabel && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-3 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">
              <PiClockLight className="h-3.5 w-3.5" />
              {t('When')}
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {startLabel}
              {endLabel && !event.all_day && (
                <span className="text-gray-400"> – {endLabel}</span>
              )}
            </p>
            {!event.all_day && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                {dayjs.tz?.guess?.() || ''}
              </p>
            )}
          </div>
        )}

        {event.location && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-3 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">
              <PiMapPinLight className="h-3.5 w-3.5" />
              {t('Location')}
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-200 truncate" title={event.location}>{event.location}</p>
          </div>
        )}

        {hasSource && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-3 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">
              <PiCalendarBlankLight className="h-3.5 w-3.5" />
              {t('Source')}
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-200 capitalize">
              {event.source_type}
            </p>
          </div>
        )}

        {event.reminder_minutes && event.reminder_minutes > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-3 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">
              <PiClockLight className="h-3.5 w-3.5" />
              {t('Reminder')}
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {event.reminder_minutes} {t('minutes before')}
            </p>
          </div>
        )}
      </div>

      {/* Description */}
      {event.description && (
        <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-4">
          <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1.5">
            {t('Description')}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
            {event.description}
          </p>
        </div>
      )}

      {/* People with access */}
      {people.length > 0 && (
        <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={() => setPeopleOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors text-left"
          >
            <PiUsersLight className="h-4 w-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex-1">
              {t('People with access')} ({people.length})
            </span>
            <PiCaretDownLight
              className={`h-4 w-4 text-gray-400 transition-transform ${peopleOpen ? '' : '-rotate-90'}`}
            />
          </button>
          {peopleOpen && (
          <div className="p-2 space-y-1">
            {[...people]
              .sort((a: any, b: any) => {
                const rank = (x: any) => {
                  const r = (x.role || '').toLowerCase();
                  const a2 = (x.access || '').toLowerCase();
                  if (r === 'creator' || r === 'owner' || a2 === 'admin') return 0;
                  if (a2 === 'edit') return 1;
                  return 2;
                };
                return rank(a) - rank(b);
              })
              .map((p: any, i: number) => {
                const name = p.name || p.Name || p.email || p.Email || '';
                const email = p.email || p.Email || '';
                const initials = p.initials
                  || (name ? name.split(' ').map((s: string) => s.charAt(0)).join('').slice(0, 2).toUpperCase() : '??');
                const accessRaw = (p.access || '').toLowerCase();
                const role = (p.role || '').toLowerCase();
                const isOwner = role === 'creator' || role === 'owner' || accessRaw === 'admin';
                const accessLabel = isOwner
                  ? t('Owner')
                  : accessRaw === 'edit'
                    ? t('Edit')
                    : accessRaw === 'view'
                      ? t('View')
                      : accessRaw
                        ? accessRaw.charAt(0).toUpperCase() + accessRaw.slice(1)
                        : '';
                const accessColor: any = isOwner ? 'indigo' : accessRaw === 'edit' ? 'blue' : 'gray';
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 ${
                      isOwner ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''
                    }`}
                  >
                    <ProfileImage
                      profilePicture={p.profile_picture || null}
                      email={email}
                      initials={initials}
                      name={name}
                      size="w-8 h-8"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{name}</p>
                      {email && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">{email}</p>}
                    </div>
                    {accessLabel && (
                      <Badge color={accessColor}>{accessLabel}</Badge>
                    )}
                  </div>
                );
              })}
          </div>
          )}
        </div>
      )}

      {/* Tags */}
      {(event.tags?.length || 0) > 0 && (
        <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <PiTagLight className="h-4 w-4 text-gray-400" />
            <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t('Tags')}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {event.tags!.map((tag: string) => (
              <Badge key={tag} color="indigo">{tag}</Badge>
            ))}
          </div>
        </div>
      )}

      </div>{/* /Scrollable middle */}

      {/* Actions (sticky at bottom) */}
      <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2 flex-shrink-0">
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Close')}
        </Button>
        {hasSource && (
          <Button
            variant="primary"
            size="small"
            onClick={() => onNavigate(event)}
            icon={<PiArrowSquareOutLight className="h-4 w-4" />}
          >
            {sourceLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Calendar View Page
   ═══════════════════════════════════════════════════════════════════════ */

function CalendarViewPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const { events, loading, filters, syncStatus, feedToken } = useAppSelector(
    (state: any) => state.calendar
  );

  const [calendarViewMode, setCalendarViewMode] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [showFeedUrl, setShowFeedUrl] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'calendar',
    steps: calendarTourSteps,
    readyToShow: !loading,
    autoStartDelay: 600,
  });

  /* ─── Callbacks ─── */

  const handleRangeChange = useCallback(
    (start: Date, end: Date) => {
      dispatch(fetchCalendarEvents(start, end, filters));
    },
    [dispatch, filters]
  );

  const navigateToSource = useCallback(
    (event: CalendarEvent) => {
      dispatch(hideModal());
      if (event.source_type === 'task' && event.source_uuid) {
        history.push(`/tasks/${event.source_uuid}`);
      } else if (event.source_type === 'matter' && event.source_uuid) {
        history.push(`/matters/${event.source_uuid}`);
      } else if (event.source_type === 'project' && event.source_uuid) {
        history.push(`/projects/${event.source_uuid}`);
      }
    },
    [dispatch, history]
  );

  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      dispatch(
        showModal({
          showModal: true,
          type: 'custom',
          message: event.title || 'Event Details',
          width: '50%',
          content: (
            <EventDetailContent
              event={event}
              onNavigate={navigateToSource}
              onClose={() => dispatch(hideModal())}
            />
          ),
        } as any)
      );
    },
    [dispatch, navigateToSource]
  );

  const handleEventDrop = useCallback(
    (eventId: string, newDeadline: Date) => {
      const event = events.find(
        (e: CalendarEvent) => String(e.id) === eventId || e.uuid === eventId
      );
      if (event) {
        dispatch(updateCalendarEvent(event.uuid, { start_time: newDeadline.toISOString() }));
      }
    },
    [dispatch, events]
  );

  const getDateRange = useCallback(() => {
    if (calendarViewMode === 'month') {
      return {
        start: currentDate.startOf('month').startOf('week').toDate(),
        end: currentDate.endOf('month').endOf('week').toDate(),
      };
    } else if (calendarViewMode === 'week') {
      return {
        start: currentDate.startOf('week').toDate(),
        end: currentDate.endOf('week').toDate(),
      };
    } else if (calendarViewMode === 'day') {
      return {
        start: currentDate.startOf('day').toDate(),
        end: currentDate.endOf('day').toDate(),
      };
    }
    return {
      start: currentDate.startOf('year').toDate(),
      end: currentDate.endOf('year').toDate(),
    };
  }, [calendarViewMode, currentDate]);

  /* ─── Toolbar handlers ─── */

  const handleExport = async () => {
    const range = getDateRange();
    const blob = await exportCalendarICS(range.start, range.end);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pons-calendar-${dayjs().format('YYYY-MM-DD')}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      dispatch(importCalendarICS(file));
      setTimeout(() => {
        const range = getDateRange();
        dispatch(fetchCalendarEvents(range.start, range.end, filters));
      }, 2000);
    }
  };

  const handleSync = () => {
    dispatch(syncCalendarEvents());
    setTimeout(() => {
      const range = getDateRange();
      dispatch(fetchCalendarEvents(range.start, range.end, filters));
    }, 3000);
  };

  const handleCreateFeedToken = () => {
    dispatch(createFeedToken('My Calendar Feed'));
    setShowFeedUrl(true);
  };

  const handleFilterChange = (key: string, value: string | undefined) => {
    dispatch(setCalendarFilters({ [key]: value || undefined }));
  };

  /* ─── Extra Header actions (Sync, Export, Import, Subscribe) ─── */

  const extraActions: Action[] = useMemo(
    () => [
      {
        label: t('Sync'),
        onClick: handleSync,
        icon: (
          <PiArrowsClockwiseLight
            className={`h-4 w-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`}
          />
        ),
        variant: 'outline' as const,
        disabled: syncStatus === 'syncing',
      },
      {
        label: t('Export'),
        onClick: handleExport,
        icon: <PiDownloadSimpleLight className="h-4 w-4" />,
        variant: 'outline' as const,
      },
      {
        label: t('Import'),
        onClick: () => fileInputRef.current?.click(),
        icon: <PiUploadSimpleLight className="h-4 w-4" />,
        variant: 'outline' as const,
      },
    ],
    [t, syncStatus, handleExport, handleSync]
  );

  /* ─── Filter bar (rendered below the CalendarView header) ─── */

  const filterBar = useMemo(() => {
    return (
      <>
        {/* Color legend — always visible */}
        <div data-tour-id="calendar-legend" className="flex items-center gap-1 px-4 py-1.5 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50 overflow-x-auto">
          {LEGEND_TYPES.map((t) => {
            const colors = EVENT_TYPE_COLORS[t];
            const label = EVENT_TYPE_LABELS[t] ?? t;
            return (
              <span
                key={t}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap"
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                {label}
              </span>
            );
          })}
        </div>

        {/* Feed URL banner */}
        {showFeedUrl && feedToken && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
            <PiInfoLight className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            <span className="text-xs text-green-700 dark:text-green-300">
              {t('Subscribe URL (add to Outlook / Google Calendar):')}
            </span>
            <code className="flex-1 text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded border border-green-300 dark:border-green-700 truncate font-mono">
              {feedToken}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(feedToken)}
              className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-900/60 transition-colors"
            >
              {t('Copy')}
            </button>
            <button
              onClick={() => setShowFeedUrl(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <PiXLight className="h-4 w-4" />
            </button>
          </div>
        )}
      </>
    );
  }, [feedToken, showFeedUrl, t]);

  /* ─── Render ─── */

  return (
    <div className="flex flex-col h-full">
      <CalendarView
        tasks={events ?? []}
        currentDate={currentDate}
        onRangeChange={handleRangeChange}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        onCalendarViewModeChange={setCalendarViewMode}
        calendarViewMode={calendarViewMode}
        onDateChange={setCurrentDate}
        isDayLoading={loading}
        extraActions={extraActions}
        filterBar={filterBar}
      />

      {/* Hidden file input for .ics import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".ics"
        onChange={handleImport}
        className="hidden"
      />

      <FeatureTour
        steps={calendarTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
}

export default CalendarViewPage;